import { motion, useReducedMotion } from 'framer-motion';
import { EASE } from './constants';

interface PullQuoteBlockProps {
    quote: string;
}

/**
 * Magazine-style pull quote — sits between the hero gallery and the
 * Overview section. Renders only when `editorial_pull_quote` is populated
 * (Phase 6.1 AI enrichment writes it; editors can hand-edit later).
 *
 * Single short evocative phrase, large Didot italic, centered between
 * thin double rules, generous vertical breathing room. The quote itself
 * carries no quotation marks — they're decorative SVG glyphs in the layout.
 */
export default function PullQuoteBlock({ quote }: PullQuoteBlockProps) {
    const shouldReduceMotion = useReducedMotion();

    return (
        <section className="bg-[#fdfaf3]">
            <div className="lux-space-px mx-auto max-w-3xl py-16 lg:py-24">
                <motion.figure
                    initial={shouldReduceMotion ? undefined : { opacity: 0, y: 16 }}
                    whileInView={shouldReduceMotion ? undefined : { opacity: 1, y: 0 }}
                    viewport={{ once: true, margin: '-100px' }}
                    transition={{ duration: 0.8, ease: EASE }}
                    className="text-center"
                >
                    {/* Hairline above */}
                    <hr className="mx-auto w-12 border-t border-[#0e2727]/25" />

                    {/* Glyph */}
                    <span className="lux-didot mt-6 block text-4xl leading-none text-[#0e2727]/30" aria-hidden="true">
                        &ldquo;
                    </span>

                    {/* The quote */}
                    <blockquote className="lux-didot mt-2 text-3xl leading-tight text-[#0e2727] italic md:text-4xl lg:text-5xl">
                        {quote}
                    </blockquote>

                    {/* Hairline below */}
                    <hr className="mx-auto mt-8 w-12 border-t border-[#0e2727]/25" />
                </motion.figure>
            </div>
        </section>
    );
}
